import type { Violation } from '@project/contracts'
import { Money, Panel } from '../design/index.js'
import type { Advisory, DocumentDetail } from '../app/api.js'
import { formatDateTime } from './format.js'
import type { InsightKind } from '@project/contracts'
import { NoticeBlock } from './feedback.js'

export interface SummaryTabProps {
  readonly doc: DocumentDetail
  readonly onOpenExceptions: (violations: readonly Violation[]) => void
  /** Opens one of the three order modals over this document (docs/06 §4). */
  readonly onOpenInsight?: (kind: InsightKind) => void
}

const ApprovalStrip = ({ doc }: { doc: DocumentDetail }) => (
  <div className="approvals">
    {doc.approvals.map((step) => (
      <div
        key={step.name}
        className={
          step.state === 'COMPLETED'
            ? 'approval approval--done'
            : step.state === 'CURRENT'
              ? 'approval approval--current'
              : 'approval'
        }
      >
        <div className="approval__name">{step.name}</div>
        <div className="approval__who">{step.user ?? (step.state === 'PENDING' ? 'Not yet with anyone' : '')}</div>
        <div className="approval__when">
          {step.at ? formatDateTime(step.at) : step.state === 'CURRENT' ? 'Waiting' : ''}
        </div>
      </div>
    ))}
  </div>
)

const AdvisoryRow = ({
  advisory,
  onOpenExceptions,
  onOpenInsight,
}: {
  advisory: Advisory
  onOpenExceptions: SummaryTabProps['onOpenExceptions']
  onOpenInsight: SummaryTabProps['onOpenInsight']
}) => (
  <NoticeBlock tone={advisory.tone}>
    <span>{advisory.text}</span>{' '}
    {advisory.violations && advisory.violations.length > 0 ? (
      <button type="button" className="link-button" onClick={() => onOpenExceptions(advisory.violations ?? [])}>
        {advisory.actionLabel ?? 'Check exception details'}
      </button>
    ) : advisory.insight && onOpenInsight ? (
      <button type="button" className="link-button" onClick={() => onOpenInsight(advisory.insight!)}>
        {advisory.actionLabel ?? 'Open'}
      </button>
    ) : advisory.href ? (
      <a href={advisory.href}>{advisory.actionLabel ?? 'Open'}</a>
    ) : null}
  </NoticeBlock>
)

export const SummaryTab = ({ doc, onOpenExceptions, onOpenInsight }: SummaryTabProps) => (
  <>
    <Panel title="Approval progress">
      {doc.approvals.length === 0 ? (
        <p className="helper">This document has not been submitted for approval yet.</p>
      ) : (
        <ApprovalStrip doc={doc} />
      )}
    </Panel>

    <Panel title={doc.party?.label ?? 'This document'} aside={doc.headline}>
      <div className="row row--wrap" style={{ alignItems: 'flex-end', gap: 32 }}>
        <div>
          <div className="label">{doc.party ? 'Party' : 'Reference'}</div>
          <div className="figure figure--small">{doc.party?.label ?? doc.documentNo ?? 'Not numbered yet'}</div>
          {doc.party?.sublabel ? <div className="helper">{doc.party.sublabel}</div> : null}
        </div>
        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
          <div className="label">Total</div>
          <div className="figure"><Money value={doc.total} size={28} /></div>
        </div>
      </div>
    </Panel>

    {/*
      docs/02 §4a separates the two: anything wrong with the document sits under
      a red "Needs Attention", and the analytical links sit under "Information".
      One list of eight notices with the important one third is a list nobody
      reads.
    */}
    {doc.advisories.some((a) => a.tone !== 'info') ? (
      <Panel title="Needs attention">
        <div className="stack">
          {doc.advisories.filter((a) => a.tone !== 'info').map((advisory) => (
            <AdvisoryRow
              key={advisory.key}
              advisory={advisory}
              onOpenExceptions={onOpenExceptions}
              onOpenInsight={onOpenInsight}
            />
          ))}
        </div>
      </Panel>
    ) : null}

    {doc.advisories.some((a) => a.tone === 'info') ? (
      <Panel title="Information">
        <div className="stack">
          {doc.advisories.filter((a) => a.tone === 'info').map((advisory) => (
            <AdvisoryRow
              key={advisory.key}
              advisory={advisory}
              onOpenExceptions={onOpenExceptions}
              onOpenInsight={onOpenInsight}
            />
          ))}
        </div>
      </Panel>
    ) : null}

    <Panel title="Details">
      {doc.details.length === 0 ? (
        <p className="helper">Nothing recorded yet.</p>
      ) : (
        <dl className="detail-list">
          {doc.details.map((entry) => (
            <div key={entry.label} style={{ display: 'contents' }}>
              <dt>{entry.label}</dt>
              <dd>{entry.value || 'Not set'}</dd>
            </div>
          ))}
        </dl>
      )}
    </Panel>

    <Panel title="Amount summary">
      <table className="totals">
        <tbody>
          {doc.amounts.map((line) => (
            <tr key={line.label} className={line.emphasis ? 'is-total' : undefined}>
              <td>{line.label}</td>
              <td><Money value={line.amount} /></td>
            </tr>
          ))}
          <tr className="is-total">
            <td>Total</td>
            <td><Money value={doc.total} size={17} /></td>
          </tr>
        </tbody>
      </table>
    </Panel>
  </>
)
